import { motion } from "framer-motion";
import { FileSpreadsheet, ScanSearch, Gauge, Brain } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { useApp } from "@/context/AppProviders";

const stepIcons = [FileSpreadsheet, ScanSearch, Gauge, Brain];

const AIAssistant = () => {
  const { t } = useApp();
  const ai = t.ai;

  return (
    <section
      id="ai"
      data-testid="ai-section"
      className="py-24 sm:py-32 border-t border-border"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.5 }}
          className="max-w-3xl"
        >
          <p className="font-mono text-sm uppercase tracking-widest text-blue-500">
            {ai.label}
          </p>
          <span className="mt-3 inline-flex items-center gap-1.5 rounded-sm bg-emerald-500/15 px-2.5 py-1 font-mono text-xs font-semibold uppercase tracking-wide text-emerald-400">
            {ai.tierBadge}
          </span>
          <h2 className="font-heading mt-3 text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight">
            {ai.title}
          </h2>
          <p className="mt-4 text-base leading-relaxed text-muted-foreground">
            {ai.subtitle}
          </p>
        </motion.div>

        {/* Philosophy: a helper, not a replacement */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.5 }}
          data-testid="ai-philosophy"
          className="mt-12 rounded-sm border border-blue-600/30 bg-blue-600/[0.06] p-6 sm:p-8"
        >
          <h3 className="font-heading text-lg sm:text-xl font-semibold tracking-tight">
            {ai.philosophy.title}
          </h3>
          <p className="mt-3 max-w-3xl text-sm sm:text-base leading-relaxed text-muted-foreground">
            {ai.philosophy.intro}
          </p>
          <div className="mt-6 grid gap-4 md:grid-cols-3">
            {ai.philosophy.points.map((pt, i) => (
              <div
                key={i}
                data-testid={`ai-philosophy-point-${i + 1}`}
                className="rounded-sm border border-border bg-card p-5"
              >
                <h4 className="font-medium tracking-tight">{pt.title}</h4>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                  {pt.desc}
                </p>
              </div>
            ))}
          </div>
        </motion.div>

        <div className="mt-14 grid gap-12 lg:grid-cols-2 lg:items-start">
          {/* How it works */}
          <div>
            <h3 className="font-heading text-lg font-medium tracking-tight">
              {ai.stepsTitle}
            </h3>
            <div className="mt-6 space-y-5">
              {ai.steps.map((step, i) => {
                const Icon = stepIcons[i];
                return (
                  <motion.div
                    key={step.title}
                    initial={{ opacity: 0, x: -16 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true, margin: "-40px" }}
                    transition={{ duration: 0.4, delay: i * 0.08 }}
                    data-testid={`ai-step-${i + 1}`}
                    className="flex gap-4 rounded-sm border border-border bg-card p-5"
                  >
                    <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-sm border border-blue-600/30 bg-blue-600/10 text-blue-500">
                      <Icon className="h-5 w-5" strokeWidth={2} />
                    </div>
                    <div>
                      <h4 className="font-medium">
                        <span className="mr-2 font-mono text-xs text-blue-500">
                          {String(i + 1).padStart(2, "0")}
                        </span>
                        {step.title}
                      </h4>
                      <p className="mt-1 text-sm leading-relaxed text-muted-foreground">
                        {step.desc}
                      </p>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </div>

          {/* FAQ */}
          <div>
            <h3 className="font-heading text-lg font-medium tracking-tight">
              {ai.faqTitle}
            </h3>
            <div className="mt-6 rounded-sm border border-border bg-card px-6">
              <Accordion type="single" collapsible className="w-full">
                {ai.faq.map((item, i) => (
                  <AccordionItem
                    key={i}
                    value={`ai-faq-${i}`}
                    data-testid={`ai-faq-${i}`}
                    className="border-border"
                  >
                    <AccordionTrigger className="py-4 text-sm font-medium hover:no-underline">
                      {item.q}
                    </AccordionTrigger>
                    <AccordionContent className="pb-4 text-sm leading-relaxed text-muted-foreground">
                      {item.a}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AIAssistant;
